import React, { useState, useEffect } from 'react';
import { Mail, ArrowRight, ArrowLeft, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { authService } from '@/services';
import { trackAuth, trackForm } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formStarted, setFormStarted] = useState(false);

  // Track page view and scroll depth
  useScrollTracking('ForgotPassword');

  // Track page load
  useEffect(() => {
    trackForm.formStart('forgot-password-form', 'Forgot Password Form');
  }, []);

  // Track field focus
  const handleFieldFocus = (fieldName: string) => {
    if (!formStarted) {
      setFormStarted(true);
    }
    trackForm.fieldFocus('forgot-password-form', fieldName);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Track password reset request
    trackAuth.passwordResetRequest(email);
    trackForm.formSubmit('forgot-password-form', 'Forgot Password Form', true);

    try {
      const response = await authService.forgotPassword(email);

      if (response.success) {
        setIsSubmitted(true);
      } else {
        // Even on error, we show success for security reasons
        // (prevents email enumeration)
        setIsSubmitted(true);
      }
    } catch (err: any) {
      // Still show success to prevent email enumeration
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <Navigation />

      {/* Forgot Password Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden min-h-screen flex items-center">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        }}></div>
        
        <div className="max-w-md mx-auto w-full relative z-10">
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-3xl opacity-20"></div>
            
            {/* Form Container */}
            <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-10 border border-white/20">
              
              {!isSubmitted ? (
                <>
                  {/* Header */}
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Mail className="w-8 h-8" />
                    </div>
                    <h2 className="text-3xl font-bold mb-2">Forgot Password?</h2>
                    <p className="text-gray-400">
                      No worries! Enter your email and we'll send you reset instructions.
                    </p>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Email Field */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Mail className="w-5 h-5 text-gray-400" />
                        </div>
                        <input
                          type="email"
                          id="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onFocus={() => handleFieldFocus('email')}
                          className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                          placeholder="you@example.com"
                          required
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 rounded-full font-semibold hover:shadow-2xl hover:shadow-purple-500/50 transition-all text-lg group flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <>
                          <Loader className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Reset Link
                          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Back to Sign In */}
                  <div className="mt-8 text-center">
                    <Link 
                      to="/signin" 
                      className="inline-flex items-center text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Sign In
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  {/* Success State */}
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8" />
                    </div>
                    <h2 className="text-3xl font-bold mb-2">Check Your Email</h2>
                    <p className="text-gray-400 mb-2">
                      We've sent password reset instructions to:
                    </p>
                    <p className="text-white font-semibold mb-8">{email}</p>
                    
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
                      <p className="text-sm text-gray-300 mb-4">
                        Didn't receive the email? Check your spam folder or try again.
                      </p>
                      <button
                        onClick={() => {
                          setIsSubmitted(false);
                          setError('');
                        }}
                        className="text-purple-400 hover:text-purple-300 font-semibold text-sm transition-colors"
                      >
                        Try a different email
                      </button>
                    </div>

                    <Link
                      to="/signin"
                      className="inline-flex items-center justify-center w-full bg-white/10 backdrop-blur-sm px-6 py-4 rounded-full font-semibold hover:bg-white/20 transition-all border border-white/20"
                    >
                      <ArrowLeft className="w-5 h-5 mr-2" />
                      Back to Sign In
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Additional Help */}
          {!isSubmitted && (
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-400">
                Need help?{' '}
                <a href="/contact" className="text-purple-400 hover:text-purple-300 transition-colors">
                  Contact Support
                </a>
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer variant="simple" />
    </div>
  );
}
